import React, { useState, useEffect, useRef } from 'react';
import { FaUpload } from 'react-icons/fa';
import { lerDocumentoVeiculo } from '../others/ocrDocumento';
import { salvarVeiculo } from '../others/cadastroVeiculo';
import { useToast } from './ToastContext';
import config from '../config/config';

const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'application/pdf'];
const TAMANHO_MAXIMO_LEITURA_MB = 10;
const MENSAGEM_IMPORTADO = 'Dados importados com sucesso! Verifique os dados preenchidos, pois podem ocorrer eventuais falhas de leitura.';
const ROTULOS = { placa: 'placa', renavam: 'RENAVAM', chassi: 'chassi', motor: 'motor', marcaModelo: 'marca/modelo', ano: 'ano fab./modelo', cor: 'cor' };
// Nem todo documento traz o número do motor: se não vier, não é tratado como falha de leitura
const OPCIONAIS = ['motor'];

/**
 * Botão "Importar CRLV-e / ATPV / CRV": lê o documento do veículo (PDF, JPG ou PNG) no navegador, de graça e sem
 * enviar o arquivo a lugar nenhum. Grava placa, chassi, RENAVAM, motor, ano fabricação/modelo e marca/modelo no cadastro
 * de veículos (para puxar depois pela placa) e devolve os dados para a tela preencher só os campos que ela tem.
 *
 * - onDadosLidos({ placa, chassi, renavam, motor, marcaModelo, anoFabricacaoModelo, cor }, duvidas): a tela preenche o que usar.
 *   placa vem só com letras e números (ABC1D23); a tela formata como quiser.
 * - gravar: false só preenche a tela, sem gravar (ex.: formulário de edição, que grava ao clicar em Salvar).
 */
function ImportarDocumentoVeiculo({ onDadosLidos, disabled = false, gravar = true }) {
  const { addToast } = useToast();
  const [lendo, setLendo] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [etapa, setEtapa] = useState('');
  // Aviso fixo depois de uma importação: mesmo em documento legível a leitura pode errar algum caractere
  const [aviso, setAviso] = useState('');
  const ocultarRef = useRef(null);

  useEffect(() => () => clearTimeout(ocultarRef.current), []);

  const importar = async (e) => {
    const escolhido = e.target.files[0];
    e.target.value = ''; // permite escolher o mesmo arquivo de novo
    if (!escolhido) return;

    if (!TIPOS_ACEITOS.includes(escolhido.type)) {
      addToast('error', 'Formato não suportado. Envie um PDF ou uma imagem JPG/PNG.');
      return;
    }
    if (escolhido.size > TAMANHO_MAXIMO_LEITURA_MB * 1024 * 1024) {
      addToast('error', `Arquivo muito grande. O limite é ${TAMANHO_MAXIMO_LEITURA_MB} MB.`);
      return;
    }

    clearTimeout(ocultarRef.current);
    setAviso('');
    setLendo(true);
    setProgresso(0);
    setEtapa('Iniciando...');
    let concluiu = false;
    try {
      const { dados: lido, duvidas } = await lerDocumentoVeiculo(escolhido, (percentual, mensagem) => {
        setProgresso(percentual);
        setEtapa(mensagem);
      });
      concluiu = true;
      const encontrados = Object.keys(ROTULOS).filter(campo => lido[campo]);
      if (!lido.placa && !lido.chassi) {
        addToast('warning', 'Não consegui ler a placa nem o chassi neste documento. Tente um arquivo mais nítido ou preencha manualmente.');
        return;
      }

      const dados = {
        placa: lido.placa || '',
        chassi: lido.chassi || '',
        renavam: lido.renavam || '',
        motor: lido.motor || '',
        marcaModelo: lido.marcaModelo || '',
        anoFabricacaoModelo: lido.ano || '',
        cor: lido.cor || '',
      };
      onDadosLidos(dados, duvidas);

      const gravou = gravar ? await salvarVeiculo(config.API_BASE_URL, dados) : true;
      const faltando = Object.keys(ROTULOS).filter(campo => !lido[campo] && !OPCIONAIS.includes(campo)).map(campo => ROTULOS[campo]);
      const suspeitos = duvidas.filter(campo => ROTULOS[campo]).map(campo => ROTULOS[campo]);
      const detalhes = `${faltando.length ? ` Não encontrei: ${faltando.join(', ')}.` : ''}`
        + `${suspeitos.length ? ` Confira com atenção: ${suspeitos.join(', ')} (não passou na validação).` : ''}`
        + `${gravou ? '' : ' Não foi possível gravar no cadastro de veículos.'}`;
      setAviso(`Lido: ${encontrados.map(campo => ROTULOS[campo]).join(', ')}.${detalhes}`);
      addToast(
        faltando.length || suspeitos.length || !gravou ? 'warning' : 'success',
        `${MENSAGEM_IMPORTADO}${detalhes}`
      );
    } catch (error) {
      console.error('Erro ao ler documento do veículo:', error);
      addToast('error', 'Não foi possível ler o documento. Preencha os dados manualmente.');
    } finally {
      setLendo(false);
      // Leituras rápidas terminam em ~1 s: mantém a barra em 100% por um instante para dar para ver
      ocultarRef.current = setTimeout(() => setEtapa(''), concluiu ? 1500 : 0);
    }
  };

  const bloqueado = lendo || disabled;

  return (
    <div style={estilos.container}>
      <label style={{ ...estilos.botao, ...(bloqueado ? estilos.botaoDesabilitado : {}) }}>
        <FaUpload /> {lendo ? `Lendo documento... ${progresso}%` : 'Importar CRLV-e / ATPV / CRV'}
        <input
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={importar}
          disabled={bloqueado}
          style={{ display: 'none' }}
        />
      </label>
      <span style={estilos.dica}>PDF ou foto do documento. É lido no seu computador; o arquivo não é enviado nem guardado.</span>

      {aviso && !lendo && (
        <div style={estilos.aviso} role="status">
          <strong>{MENSAGEM_IMPORTADO}</strong>
          <span>{aviso}</span>
        </div>
      )}

      {etapa && (
        <div style={estilos.progresso} role="progressbar" aria-valuenow={progresso} aria-valuemin={0} aria-valuemax={100}>
          <div style={estilos.trilho}>
            <div style={{ ...estilos.barra, width: `${progresso}%` }} />
          </div>
          <span style={estilos.dica}>{etapa} {progresso}%</span>
        </div>
      )}
    </div>
  );
}

const estilos = {
  container: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' },
  botao: {
    backgroundColor: '#6f42c1', color: '#fff', padding: '8px 16px', borderRadius: '6px', fontSize: '14px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
  },
  botaoDesabilitado: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },
  dica: { fontSize: '12px', color: '#6c757d' },
  aviso: {
    flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '2px', padding: '8px 12px', borderRadius: '6px',
    backgroundColor: '#fff8e1', border: '1px solid #f0c36d', color: '#7a5a00', fontSize: '12px',
  },
  progresso: { flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '4px' },
  trilho: { height: '10px', borderRadius: '5px', backgroundColor: '#e3e6ea', overflow: 'hidden' },
  barra: { height: '100%', borderRadius: '5px', backgroundColor: '#6f42c1', transition: 'width 0.3s ease' },
};

export default ImportarDocumentoVeiculo;
